import { Socket } from "socket.io-client"
import { ModalityDataSource } from "../Types/ModalityDataSource"
import { PageType } from "../Types/PageType"
import { DecodedPixels, SocketResponse } from "../Types/SocketResponse"
import { decode } from "@msgpack/msgpack"
import { TraceData } from "./TimeSeriesData"
import { OrderedBuffer } from "./OrderedBuffer"
import { TimeSeriesPageManager } from "./TimeSeriesPageManager"

var pageId = 0

export type ModalityPageProperties = {
	socketId: string
	startTime: number
	endTime: number
	width: number
	patientId: string
	modalityDataSources: ModalityDataSource[]
	patientIsAdmitted: boolean
	maxReadTime: number | undefined
	getDataQuerySocket: null | ((id?: string) => Socket)
}

type SingleModalityEdges = [number | undefined, number][]
type MultipleModalityEdges = [number | undefined, number, number][]
type DecodedTimeSeriesData = {
	times: number[]
	data: number[] | (number | null)[][]
}

type CachedRender = {
	bitmap: ImageBitmap
	dirty: boolean // If we need to completely redraw using the original data
	edges: SingleModalityEdges | MultipleModalityEdges
	endTimes?: Record<string, number> // Per-modality times where previously drawn data ends
}

export type TimingInformation = {
	gapIndexes: number[]
	samplingPeriod: number | null
}

export abstract class Page<DataType, PageProperties extends ModalityPageProperties = ModalityPageProperties> {
	public socketId: string = "?"
	public id: number
	public index: number = 0
	public startTime: number = 0
	public endTime: number = 0
	public width: number = 0
	public patientId: string = "?"
	public modalityDataSources: ModalityDataSource[] = []
	public maxReadTime: number | undefined = undefined // The maximum timestamp we are allowed to read from the file. Useful for live review and analysis cache invalidation.
	public receiveDataError: string | null = null

	// TODO: Maybe these all belong in their own big object that can be accessed at once, ModalityMetadata or something like that.
	public data = new Map<number, Map<string, DataType>>() // Data Object Id, then modality.
	public renderCache = new Map<number, Map<string, CachedRender>>() // Data Object Id, then modality.
	public timingInformation = new Map<number, Map<string, TimingInformation>>() // Data Object Id, then modality

	protected patientIsAdmitted: boolean = true
	protected enforceDataPointLimit: boolean = false
	protected getDataQuerySocket: null | ((id?: string) => Socket) = null

	// Data Object ID : ["ABP", "ICP", ...]
	// When working with multiple data sources, the same "modality" can be loaded from multiple sources.
	protected dataObjectsLoaded = new Map<number, Map<string, boolean>>()
	protected dataObjectsLoading = new Map<number, Map<string, boolean>>()

	// UTC timestamps of the latest read end time that we requested and received from the backend.
	// Used in live review to make sure we don't re-request the same intervals of data in partial page loading.
	private lastRequestedPartialLoadEndTime: number | null = null

	// How far each modality's data actually got, keyed by "<dataObjectId>:<modality>".
	// A page's end time is per-modality: qEEG DSA stops short of a gap or of the live
	// edge while a line trace covers the whole window. Collapsing that into one
	// page-level value lets whichever response arrives last decide for the page. If a
	// full-range modality lands last, the page marks itself complete and the DSA tail
	// it is missing is never requested again; if a short one lands last, modalities
	// that already finished get re-requested.
	private receivedEndTimes = new Map<string, number>()

	// Streams whose final chunk (order === total) has arrived. Only a completed
	// stream can say whether its modality truly reached the requested end; an
	// in-flight stream's end time only reflects the chunks received so far.
	private completedStreams = new Set<string>()
	private widthOnRequest: number = 0 // Keeps track of the resolution that we requested the data, needed to reload when screen size changes.

		// When the page loads partially, call this function.
	protected onPartialLoad: ((page: Page<DataType>) => void) | null = null
	protected onFinishedLoading: ((page: Page<DataType>) => void) | null = null
	protected onLoadEarlyExit: (() => void) | null = null

	public hasNotReceivedFullDataRange: boolean = true
	public responseBuffers = new Map<string, OrderedBuffer<SocketResponse>>() // Asynchronous packets need to be sorted into the correct order on arrival.
	private validRequestIds: Set<string> = new Set() // If a request comes back and the ID is not in the set, then we ignore it.
	private pageManager: TimeSeriesPageManager<any, any>

	constructor(pageManager: TimeSeriesPageManager<any, any>) {
		this.id = pageId++
		this.pageManager = pageManager
	}
	
	abstract getType(): PageType

	get loaded() {
		return this.modalityDataSources.every(dataSource => (
			this.dataObjectsLoaded.get(dataSource.dataObjectId)?.get(dataSource.modality) === true
			&& this.data.get(dataSource.dataObjectId)?.get(dataSource.modality) !== undefined
			)
		)
	}

	get loading() {
		return this.modalityDataSources.some(dataSource => (
			this.dataObjectsLoading.get(dataSource.dataObjectId)?.get(dataSource.modality) === true)
		)
	}

	get finishedLoading() {
		for (const buffer of this.responseBuffers.values()) {
			if (!buffer.isCompleted()) {
				return false
			}
		}

		return true
	}

	public needsToBeLoaded() {
		if (this.receiveDataError || this.loading || this.modalityDataSources.length === 0) {
			return false
		}

		return (
			!this.loaded 
			|| this.hasNotReceivedFullDataRange 
			|| (this.widthOnRequest > 0 && this.widthOnRequest !== this.width))
	}

	updateProperties(properties: PageProperties) {
		Object.assign(this, properties)
	}

	socketEventName = () => "render_modalities"

	requestData(batchedDataSources: Map<number, ModalityDataSource[]>, socket: Socket, readStartTime: number, readEndTime: number) {
		if (this.getDataQuerySocket === null) {
			return
		}

		const requestId = `${this.socketId}-${socket.id}-${Date.now()}-${Math.random()}-${Math.random()}` // Can't use crypto.randomUUID because of TS compilation failure.

		this.validRequestIds = new Set()
		

		this.modalityDataSources.forEach(modalityDataSource => {
			const existingBuffer = this.responseBuffers.get(modalityDataSource.modality)
			if (existingBuffer) {
				existingBuffer.clear()
			} else {
				this.responseBuffers.set(modalityDataSource.modality, new OrderedBuffer())
			}
		})

		// The number of pixels that we request should be proportional to how much of the page this section takes up.
		let ratio = (readEndTime - readStartTime) / (this.endTime - this.startTime)
		
		// clamp it between 0 and 1
		ratio = Math.min(1, Math.max(0, ratio)) 

		// at least two pixels should be requested, even if the ratio is very small. Increase for more frequency resolution.
		// This allows us to (as performantly as possible) visualize high resolution data in real-time with a large viewing window.
		// If there are too many pixels, the page will be re-requested and downsampled by the backend.
		const width = Math.max(4, Math.ceil(ratio * this.width))

		batchedDataSources.forEach((dataSources, dataObjectId) => {
			socket.emit(
				this.socketEventName(), 
				this.patientId, 
				dataObjectId, 
				this.id, 
				dataSources, 
				readStartTime, 
				readEndTime,
				width, 
				this.patientIsAdmitted,
				requestId
			)
		})

		this.lastRequestedPartialLoadEndTime = readEndTime
		this.widthOnRequest = this.width
		this.validRequestIds.add(requestId)
	}

	receiveSocketResponse(response: SocketResponse, unsubscribe: () => void) {
		if (!this.validRequestIds.has(response.request_id)) {
			// Ignore any responses that we weren't expecting.
			return
		}

		// Check for errors before storing the data in the buffer.
		if (response.error) {
			this.loadFailed(response.error, unsubscribe)
			return
		}

		// We have to buffer the responses because they can return out of order.
		let modalityResponseBuffer = this.responseBuffers.get(response.modality)

		if (modalityResponseBuffer === undefined) {
			console.warn(`${this.id} Response buffer should have been created before requesting the data`)
			return
		}

		modalityResponseBuffer.setMaxId(response.total)
		const readyResponses = modalityResponseBuffer.add(response.order, response)

		let isFinished = false
		let propagateBackward = false
		let propagateForward = false

		try {
			for (let i = 0; i < readyResponses.length; i++) {
				const readyResponse = readyResponses[i]
				const { dataObjectDataMap, timingInformationMap } = this.setUpDataStorage(readyResponse)
				const oldData = dataObjectDataMap.get(readyResponse.modality) as TraceData | undefined

				// A partial re-request resumes the whole page from the earliest point
				// any modality reached, so a modality that already got further receives
				// its tail again. The merge and the gap bookkeeping both assume each
				// sample arrives exactly once, in order, so drop anything at or before
				// the last sample already stored -- before the response buffer counts it.
				const [data, packetTimes] = this.dropAlreadyLoadedSamples(decode(readyResponse.pixels) as DecodedPixels, oldData, response.is_timestamp_compression_enabled)
				const dataLength = data.length > 0 && Array.isArray(data[0]) ? data[0].length : data.length
				const [times, knownGaps, knownSamplingPeriod] = this.extractTimes(packetTimes, dataLength, modalityResponseBuffer, response.is_timestamp_compression_enabled)

				this.handleDataConversionInPlace(data, readyResponse.conversion_factor)

				const newData: DecodedTimeSeriesData = { data, times }
				const traceData = this.combineTimeSeriesData(oldData, newData)

				if (this.enforceDataPointLimit && traceData.times.length > 4 * this.width) {
					throw new Error("too many pixels!")
				}

				dataObjectDataMap.set(readyResponse.modality, traceData as unknown as DataType)
				this.updateTimingInformation(timingInformationMap, knownGaps, knownSamplingPeriod, readyResponse.modality, traceData)

				// If we are loading data on the edges of the page, invalidate the cache of the previous and next page
				if (readyResponse.order === 1) {
					propagateBackward = true
				}

				if (readyResponse.order === readyResponse.total) {
					propagateForward = true
				}

				// Prevent re-rendering until we have processed all the buffered data we have.
				if (i === readyResponses.length - 1) {
					modalityResponseBuffer.updateUnloadedStartTime(readyResponse.chunk_end_time/1000)
					this.invalidateModalityRenderCache(readyResponse.modality, propagateBackward, propagateForward)
					isFinished = this.updateLoadedState(readyResponse)
				}
			}

			if (isFinished) {
				unsubscribe()
			}

		} catch (e) {
			this.loadFailed(`${e}`, unsubscribe)
		}
	}

	private loadFailed(error: string, unsubscribe: () => void) {
		const errorClean = `${error}`
		this.receiveDataError = errorClean
		console.error(errorClean)
		unsubscribe()
		this.onFinishedLoading?.(this)
	}

	public unload = () => {
		this.validRequestIds = new Set()
		this.responseBuffers.clear()
		this.lastRequestedPartialLoadEndTime = null
		this.receivedEndTimes.clear()
		this.completedStreams.clear()
		this.data.clear()
		this.dataObjectsLoaded.clear()
		this.dataObjectsLoading.clear()
		this.clearRenderCache()
		this.receiveDataError = null
	}

	public cancel = () => {
		if (this.validRequestIds.size > 0) {
			const socket = this.getDataQuerySocket?.(this.socketId) // Make sure the cancel request gets sent on the same send buffer as the load request.
			socket?.emit("cancel_render_modalities", [...this.validRequestIds])
		}

		this.unload()
	}

	public clearRenderCache = () => {
		this.renderCache.forEach(dataObjectCache => {
			dataObjectCache.forEach(cache => cache.bitmap.close())
			dataObjectCache.clear()
		})
		this.renderCache.clear()
	}

	public invalidateModalityRenderCache(modality: string, propagateBackward: boolean, propagateForward: boolean) {
		this.renderCache.forEach(dataObjectCache => {
			dataObjectCache.forEach((cache, key) => {
				if (key.includes(modality)) {
					cache.dirty = true
				}
			})
		})

		if (propagateBackward) {
			const prevPage = this.pageManager.getPreviousPage(this)
			prevPage?.invalidateModalityRenderCache(modality, false, false)
		}

		if (propagateForward) {
			const nextPage = this.pageManager.getNextPage(this)
			nextPage?.invalidateModalityRenderCache(modality, false, false)
		}
	}

	public invalidateRenderCacheKey = (key: string) => {
		this.renderCache.forEach(dataObjectCache => { 
			const cache = dataObjectCache.get(key)

			if (cache) {
				cache.dirty = true
			}
		})
	}

	public updateRenderCache = (dataObjectId: number, key: string, newValue: CachedRender) => {
		if (!this.renderCache.has(dataObjectId)) {
			this.renderCache.set(dataObjectId, new Map())
		}

		const cachedRender = this.renderCache.get(dataObjectId)?.get(key)

		if (cachedRender) {
			cachedRender.bitmap.close()
		}

		this.renderCache.get(dataObjectId)?.set(key, newValue)
	}

	public load = () => {
		if (!this.needsToBeLoaded() || this.width <= 0 || this.getDataQuerySocket === null || (this.patientIsAdmitted && !this.maxReadTime)) {
			this.onLoadEarlyExit?.()
			return
		}
		
		if (this.widthOnRequest > 0 && this.widthOnRequest !== this.width) {
			// The current data is requested at the wrong resolution and must be re-requested.
			this.unload()
		}

		const [readStartTime, readEndTime] = this.getReadStartAndEndTime()

		if (readEndTime <= readStartTime) {
			this.onLoadEarlyExit?.()
			return
		}

		// Data Object ID: [{ modality: "ABP"}, { modality: "ICP" }, { modality: "PRx", onDemandAnalysis: {...} } ...]
		// Making one socket call with all of the modalities is more efficient than sending each one separately.
		// e.g. reading 7 modalities only requires setting everything up one time instead of 7 times.
		const batchedModalitiesByDataObjectId = new Map<number, ModalityDataSource[]>()

		// Prevent asking for identical data multiple times (e.g. different indexes of the same composite data, like MAPopt)
		const uniqueDataSources = Array.from(new Map(this.modalityDataSources.map(dataSource => [this.getModalityDataSourceKey(dataSource), dataSource])).values())

		const dataSourcesToLoad = uniqueDataSources
			.filter(dataSource => {
				const key = this.getModalityDataSourceKey(dataSource)
				return !this.dataObjectsLoaded.get(dataSource.dataObjectId)?.get(key) && !this.dataObjectsLoading.get(dataSource.dataObjectId)?.get(key)
			})

		const socketEventName = this.socketEventName()

		dataSourcesToLoad.forEach(dataSource => {
			// Add to the list of modalities
			const existingList = batchedModalitiesByDataObjectId.get(dataSource.dataObjectId) ?? []
			batchedModalitiesByDataObjectId.set(dataSource.dataObjectId, [...existingList, dataSource ])

			// Update the loading state
			const existingLoadingModalities = this.dataObjectsLoading.get(dataSource.dataObjectId) ?? new Map()
			this.dataObjectsLoading.set(dataSource.dataObjectId, existingLoadingModalities.set(dataSource.modality, true))
		})

		const socket = this.getDataQuerySocket(this.socketId)

		const allListeners: Array<(socketResponse: SocketResponse) => void> = []

		batchedModalitiesByDataObjectId.forEach((_, dataObjectId) => {
			const listener = (socketResponse: SocketResponse) => {
				if (socketResponse.page_id === this.id && socketResponse.data_object_id === dataObjectId) {
					this.receiveSocketResponse(socketResponse, () => socket.off(socketEventName, listener))
				}
			}

			allListeners.push(listener)
			socket.on(socketEventName, listener)
		})

		const cancelLoad = () => {
			allListeners.forEach(listener => socket.off(socketEventName, listener))
			this.dataObjectsLoading.forEach(modalityMap => {
				modalityMap.forEach((_, modality) => {
					modalityMap.set(modality, false)
				})
			})
		}

		try {
			this.requestData(batchedModalitiesByDataObjectId, socket, readStartTime, readEndTime)
		} catch (e) {
			console.error(e)
			cancelLoad()
		}
	}

	private getReadStartAndEndTime() {
		// Only request data that we haven't already asked for and received
		let readStartTime = this.startTime
		let readEndTime = this.endTime

		if (this.lastRequestedPartialLoadEndTime) {
			readStartTime = Math.max(this.startTime, this.lastRequestedPartialLoadEndTime) 
		}

		if (this.maxReadTime && this.maxReadTime < this.endTime) {
			if (this.patientIsAdmitted) {
				// In live-review mode, make sure the partial loads go out on a grid. 
				// We need to make sure that each request (2px) is at least the same amount of time.
				// Otherwise, we end up with requests for 500ms of data and requests for 3s of data both getting 2 pixels, which makes it look weird.
				const maxReadTimeOnGrid = Math.ceil(this.maxReadTime / 1000) * 1000
				readEndTime = Math.min(maxReadTimeOnGrid, this.endTime)
			} else {
				// Otherwise, make sure to limit by the max read time (file end time).
				readEndTime = Math.min(this.maxReadTime, this.endTime)
			}
		}

		return [readStartTime, readEndTime]
	}

	private getModalityDataSourceKey = (dataSource: ModalityDataSource) => `${dataSource.modality}-${dataSource.dataObjectId}`

	// Timestamps are compressed into continuous chunks of timestamps to save bits.
	// This function just de-compresses the timestamps back into a list of the actual timestamp values.
	// Drops the leading samples of a response that are at or before the last
	// sample already stored for this modality. Samples arrive in time order, so
	// the overlap is always a prefix. Trimming at the packet level, before
	// extractTimes feeds the response buffer, keeps the buffer's cumulative
	// sample count -- and with it the gap indexes -- consistent with the merged
	// data arrays.
	private dropAlreadyLoadedSamples([data, packetTimes]: DecodedPixels, oldData: TraceData | undefined, isTimestampCompressionEnabled: boolean): DecodedPixels {
		const oldTimes = oldData?.times
		if (!oldTimes || oldTimes.length === 0) {
			return [data, packetTimes]
		}
		const cutoffMicroseconds = oldTimes[oldTimes.length - 1] * 1000

		if (!isTimestampCompressionEnabled) {
			const timesMicroseconds = packetTimes as number[]
			let dropCount = timesMicroseconds.findIndex(time => time > cutoffMicroseconds)
			if (dropCount === -1) {
				dropCount = timesMicroseconds.length
			}
			return dropCount === 0 ? [data, packetTimes] : [this.sliceLeadingSamples(data, dropCount), timesMicroseconds.slice(dropCount)]
		}

		// Drop the runs that end at or before the cutoff...
		const runs = (packetTimes as number[][]).slice()
		let dropCount = 0

		while (runs.length > 0) {
			const [, endMicroseconds, numberOfPoints] = runs[0]
			if (endMicroseconds === undefined || numberOfPoints === undefined || endMicroseconds > cutoffMicroseconds) {
				break
			}
			dropCount += numberOfPoints
			runs.shift()
		}

		// ...then split the one run that straddles it, moving the run start to the
		// first kept sample (the same interpolation extractTimes uses).
		const [startMicroseconds, endMicroseconds, numberOfPoints] = runs[0] ?? []
		if (startMicroseconds !== undefined && startMicroseconds <= cutoffMicroseconds && numberOfPoints > 1) {
			const step = (endMicroseconds - startMicroseconds) / (numberOfPoints - 1)
			const dropped = Math.min(numberOfPoints - 1, Math.floor((cutoffMicroseconds - startMicroseconds) / step) + 1)
			runs[0] = [startMicroseconds + dropped * step, endMicroseconds, numberOfPoints - dropped]
			dropCount += dropped
		}

		return dropCount === 0 ? [data, packetTimes] : [this.sliceLeadingSamples(data, dropCount), runs]
	}

	private sliceLeadingSamples(data: DecodedPixels[0], dropCount: number): DecodedPixels[0] {
		if (data.length > 0 && Array.isArray(data[0])) {
			return (data as (number | null)[][]).map(row => row.slice(dropCount))
		}
		return (data as number[]).slice(dropCount)
	}

	private extractTimes(packetTimes: number[] | number[][], dataLength: number, buffer: OrderedBuffer<SocketResponse>, isTimestampCompressionEnabled: boolean): [Array<number>, Array<number>, number] {
		if (!isTimestampCompressionEnabled) {
			const timesMilliseconds = (packetTimes as number[]).map(microseconds => microseconds/1000)
			return [timesMilliseconds, [], Infinity]
		}
		
		const times = new Array<number>(dataLength).fill(0)
		let timesOffset = 0

		for (const [startTimeMicroseconds, endTimeMicroseconds, numberOfPoints] of packetTimes as number[][]) {
			if (startTimeMicroseconds === undefined || endTimeMicroseconds === undefined || numberOfPoints === undefined) {
				continue
			}

			const start = startTimeMicroseconds / 1e3
			const end = endTimeMicroseconds / 1e3

			buffer.addTimingRecord(start, end, numberOfPoints)

			if (numberOfPoints === 1) {
				times[timesOffset++] = end
				continue
			}
			
			const step = (end - start) / (numberOfPoints - 1)

			for (let i = 0; i < numberOfPoints; i++) {
				times[timesOffset++] = start + i * step
			}
		}

		return [times, buffer.knownGaps, buffer.knownSamplingPeriod]
	}

	// For data compression purposes, data can come over the network as integers, even if the data is floating point.
	// This offers a significant performance gain because we can use fewer bits to represent approximately the same number.
	// The conversion factor is the number that we multiply by to get the floating point number out of the integer.
	private handleDataConversionInPlace(data: number[] | (number | null)[][], conversionFactor: number) {
		if (conversionFactor === 1) {
			return
		}

		switch(this.getType()) {
			case PageType.EEG:
				const eegData = data as number[][]

				for (let i = 0; i < eegData.length; i++) {
					for (let j = 0; j < eegData[i].length; j++) {
						eegData[i][j] *= conversionFactor
					}
				}

				break
			default:
				const timeSeriesData = data as number[]

				for (let i = 0; i < data.length; i++) {
					timeSeriesData[i] *= conversionFactor
				}
		}
	}

	private combineTimeSeriesData(oldData: TraceData | undefined, newData: DecodedTimeSeriesData | undefined): TraceData {
		if (oldData === undefined) {
			oldData = { data: new Float32Array(), times: [] }
		}

		if (newData === undefined || newData.data.length === 0) {
			return oldData
		}

		const isComposite = Array.isArray(newData.data[0])

		if (oldData.data.length === 0) {
			if (isComposite) {
				return {
					times: newData.times,
					data: (newData.data as number[][]).map(column => this.mergeData(new Float32Array(), column as (number | null)[])),
				}
			}

			return {
				times: newData.times,
				data: this.mergeData(new Float32Array(), newData.data as (number | null)[]),
			}
		}

		const data: Float32Array | Float32Array[] = isComposite
			? (oldData.data as Float32Array[]).map((oldColumn, i) => this.mergeData(oldColumn, newData.data[i] as (number | null)[]))
			: this.mergeData(oldData.data as Float32Array, newData.data as (number | null)[])

		return { times: [...oldData.times, ...newData.times], data } as TraceData
	}

	private mergeData(oldColumn: Float32Array | undefined, newColumn: (number | null)[] | undefined): Float32Array {
		if (!oldColumn || !newColumn) {
			return new Float32Array()
		}

		const combinedArray = new Float32Array(oldColumn.length + newColumn.length)

		// Copy old data
		combinedArray.set(oldColumn)

		// Map new data, replacing null with NaN
		for (let j = 0; j < newColumn.length; j++) {
			combinedArray[oldColumn.length + j] = newColumn[j] === null ? NaN : newColumn[j]!
		}

		return combinedArray
	}

	public setSocketAccessor(getDataQuerySocket: null | ((id?: string) => Socket)) {
		this.getDataQuerySocket = getDataQuerySocket
	}

	// The maximum read time is the farthest that the page is allowed to read.
	// This is to prevent reading and caching analytics for data that doesn't exist yet.
	public updateMaxReadTime(newMaxReadTimeMilliseconds: number) {
		this.maxReadTime = newMaxReadTimeMilliseconds
	}

	private getSamplingPeriod(knownSamplingPeriod: number): number | null {
		const timeBetweenPixels = (this.endTime - this.startTime) / this.width
		return Math.max(knownSamplingPeriod, timeBetweenPixels)
	}

	private setUpDataStorage(response: SocketResponse) {
		if (!this.data.has(response.data_object_id)) {
			this.data.set(response.data_object_id, new Map())
		}

		if (!this.timingInformation.has(response.data_object_id)) {
			this.timingInformation.set(response.data_object_id, new Map())
		}

		const dataObjectDataMap = this.data.get(response.data_object_id)
		const timingInformationMap = this.timingInformation.get(response.data_object_id)
		
		if (!dataObjectDataMap || !timingInformationMap) {
			throw new Error("data object data map or timing information map was undefined.")
		}

		return { dataObjectDataMap, timingInformationMap }
	}

	private updateTimingInformation(timingInformationMap: Map<string, TimingInformation>, knownGaps: number[], knownSamplingPeriod: number, modality: string, traceData: TraceData) {
		const samplingPeriod = this.getSamplingPeriod(knownSamplingPeriod)
		const previousGaps = timingInformationMap.get(modality)?.gapIndexes ?? []
		let newGaps

		// Make sure we don't add the same gap indexes twice
		if (previousGaps.length === 0 || (previousGaps.length > 0 && knownGaps.length > 0 && previousGaps[0] === knownGaps[0]))  {
			newGaps = knownGaps
		} else {
			newGaps = [...previousGaps, ...knownGaps]
		}

		timingInformationMap.set(modality, {
			gapIndexes: newGaps,
			samplingPeriod,
		})
	}

	private updateLoadedState(response: SocketResponse): boolean {
		const loading = this.dataObjectsLoading.get(response.data_object_id) ?? new Map()
		const loaded = this.dataObjectsLoaded.get(response.data_object_id) ?? new Map()
		const hasCompletedResponse = response.order === response.total

		this.updateReceivedDataState(response)
		this.updateHasNotReceivedFullDataRange()
		this.dataObjectsLoading.set(response.data_object_id, loading.set(response.modality, !hasCompletedResponse))
		this.dataObjectsLoaded.set(response.data_object_id, loaded.set(response.modality, hasCompletedResponse && !this.hasNotReceivedFullDataRange))
		
		const allBuffersCompleted = [...this.responseBuffers.values()].every(buffer => buffer.isCompleted())
		
		if (allBuffersCompleted) {
			this.onFinishedLoading?.(this)
		} else {
			this.onPartialLoad?.(this)
		}

		return allBuffersCompleted
	}

	// Pure state: record what this response actually delivered. No decisions here --
	// updateHasNotReceivedFullDataRange reads this state to make them.
	private updateReceivedDataState(response: SocketResponse) {
		const key = `${response.data_object_id}:${response.modality}`

		// data_end_time is how far the server actually produced data, in
		// microseconds like chunk_end_time; page times are milliseconds.
		const responseDataEndTime = response.data_end_time / 1000
		this.receivedEndTimes.set(key, Math.max(this.receivedEndTimes.get(key) ?? Number.NEGATIVE_INFINITY, responseDataEndTime))

		if (response.order === response.total) {
			this.completedStreams.add(key)
		}
	}

	// Business rule: the page is short if any *completed* stream stopped before the
	// full range, and resumes from the earliest point any of them reached --
	// re-reading what the others already have is cheap, whereas resuming past a
	// short modality would drop its data. In-flight streams are excluded because
	// their end times only reflect the chunks received so far; counting them would
	// fire a re-request for data that is still on its way.
	private updateHasNotReceivedFullDataRange() {
		const completedEndTimes = [...this.completedStreams].map(key => this.receivedEndTimes.get(key) ?? Number.NEGATIVE_INFINITY)

		if (completedEndTimes.length === 0) {
			return
		}

		const fullRangeEndTime = this.patientIsAdmitted
			? this.endTime
			: Math.min(this.endTime, this.maxReadTime ?? this.endTime)

		const shortestEndTime = Math.min(...completedEndTimes)
		this.hasNotReceivedFullDataRange = shortestEndTime < fullRangeEndTime

		if (this.hasNotReceivedFullDataRange) {
			this.lastRequestedPartialLoadEndTime = shortestEndTime
		}
	}

	public setOnPartialLoad = (onPartialLoad: (page: Page<any, any>) => void) => {
		this.onPartialLoad = onPartialLoad
	}

	public setOnFinishedLoading = (onFinishedLoading: (page: Page<any, any>) => void) => {
		this.onFinishedLoading = onFinishedLoading
	}

	public setOnLoadEarlyExit = (onLoadEarlyExit: () => void) => {
		this.onLoadEarlyExit = onLoadEarlyExit
	}
}
